/**
 * Per-device drill-down for the topology canvas. Click a node in the
 * single-site L2 view → this store fetches /api/topology/device/{serial}/details/
 * and the right-side panel renders it.
 *
 * Cached by serial. No TTL — the data is fresh per Meraki snapshot,
 * which is good enough for the click-to-inspect interaction. A
 * "Refresh" button on the panel re-fires the fetch.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";

export interface DevicePort {
  portId:          string | null;
  name:            string | null;
  enabled:         boolean | null;
  type:            "access" | "trunk" | string | null;
  vlan:            number | null;
  voiceVlan:       number | null;
  allowedVlans:    string | null;
  poeEnabled:      boolean | null;
  rstpEnabled:     boolean | null;
  linkNegotiation: string | null;
  accessPolicyType: string | null;
  status:          string | null;
  duplex:          string | null;
  speed:           string | null;
  errors:          string[] | null;
  warnings:        string[] | null;
  clientCount:     number | null;
  trafficKbps:     Record<string, number> | null;
  lastReportedAt:  string | null;
}

export interface DeviceClient {
  id:           string | null;
  mac:          string | null;
  description:  string | null;
  ip:           string | null;
  vlan:         number | string | null;
  switchport:   string | null;
  ssid:         string | null;
  usage:        Record<string, number> | null;
  manufacturer: string | null;
  os:           string | null;
}

export interface DeviceDetails {
  serial:      string;
  name:        string | null;
  model:       string | null;
  mac:         string | null;
  lanIp:       string | null;
  tags:        string | string[] | null;
  firmware:    string | null;
  networkId:   string | null;
  productType: "switch" | "appliance" | "wireless" | "other";
  ports?:      DevicePort[];
  clients?:    DeviceClient[];
}

interface State {
  bySerial: Record<string, DeviceDetails>;
  loading:  Record<string, true>;
  errors:   Record<string, string>;
  load:    (serial: string, opts?: { force?: boolean }) => Promise<DeviceDetails | null>;
}

export const useTopologyDeviceDetails = create<State>((set, get) => ({
  bySerial: {},
  loading:  {},
  errors:   {},

  load: async (serial: string, opts) => {
    if (!serial) return null;
    const cached = get().bySerial[serial];
    if (cached && !opts?.force) return cached;
    if (get().loading[serial]) {
      // Same dedupe pattern as the other topology stores.
      return await new Promise<DeviceDetails | null>((resolve) => {
        const t = setInterval(() => {
          const s = get();
          if (s.bySerial[serial]) {
            clearInterval(t);
            resolve(s.bySerial[serial]);
          }
        }, 80);
      });
    }
    set((s) => ({
      loading: { ...s.loading, [serial]: true },
      errors:  { ...s.errors,  [serial]: undefined as unknown as string },
    }));
    try {
      const raw = await apiGet<DeviceDetails>(`/topology/device/${encodeURIComponent(serial)}/details/`);
      set((s) => ({
        bySerial: { ...s.bySerial, [serial]: raw },
        loading:  { ...s.loading,  [serial]: undefined as unknown as true },
      }));
      return raw;
    } catch (err) {
      const message =
        err instanceof ApiError ? `API error ${err.status}`
        : err instanceof Error ? err.message
        : "unknown error";
      set((s) => ({
        loading: { ...s.loading, [serial]: undefined as unknown as true },
        errors:  { ...s.errors,  [serial]: message },
      }));
      return null;
    }
  },
}));
